import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Breadcrumb } from '@app/models/breadcrumb.model';
import { CreateSageHrConfiguration } from '@app/models/integrations/sage-hr/create-sage-hr-configuration.model';
import { SageHrConfiguration } from '@app/models/integrations/sage-hr/sage-hr-configuration.model';
import { UpdateSageHrConfiguration } from '@app/models/integrations/sage-hr/update-sage-hr-configuration';
import { SageHrConfigurationApiService } from '@app/shared/api/sage-hr-configuration-api.service';
import { SagehrApiService } from '@app/shared/api/sage-hr.api.service';
import { BreadcrumbService } from '@app/shared/breadcrumbs/breadcrumbs.service';
import { ButtonType } from '@app/shared/components/inputs/button/button.component';
import { IconHoverColor } from '@app/shared/components/inputs/table-action-icon/table-action-icon.component';
import { NotifyUtils } from '@app/shared/utils/notify.utils';
import { State } from '@app/shared/utils/state.model';
import { SwalUtils } from '@app/shared/utils/swal.utils';
import moment from 'moment';

enum WorkflowState {
  WELCOME,
  SETUP,
  SUCCESS,
  CONFIGURE
}

@Component({
  selector: 'app-integration-sagehr',
  templateUrl: './integration-sagehr.component.html',
  styleUrls: ['./integration-sagehr.component.scss']
})
export class IntegrationSagehrComponent implements OnInit {
  private readonly breadcrumb: Breadcrumb;

  eIconHoverColor = IconHoverColor;
  eButtonType = ButtonType;
  eWorkflowState = WorkflowState;
  workflowState = WorkflowState.WELCOME;
  componentState = new State(false);
  
  sageHrConfiguration?: SageHrConfiguration;

  constructor(public route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private breadcrumbService: BreadcrumbService,
    private sageHrConfigurationApiService: SageHrConfigurationApiService, 
    private sageHrApiService: SagehrApiService,
    private swalUtils: SwalUtils,
    private notifyUtils: NotifyUtils
  ) { 
    this.breadcrumb = this.breadcrumbService.init(this.route);
  }

  private readonly setupFormValidators = [Validators.required, Validators.minLength(1), Validators.maxLength(255)];
  setupForm = this.formBuilder.group({
    sageHrCompanyId: ['', this.setupFormValidators],
    apiKey: ['', this.setupFormValidators],
    
  });
  setupFormSubmitted = false;
  setUpFormState = new State(false);
  syncButtonState = new State(false);
  toggleSyncButtonState = new State(false);

  get sageHrCompanyId(): FormControl {
    return this.setupForm.controls.sageHrCompanyId as FormControl;
  }

  get apiKey(): FormControl {
    return this.setupForm.controls.apiKey as FormControl;
  }

  ngOnInit(): void {
    this.checkConfigurationExists();
  }

  ngOnDestroy() {
    this.breadcrumbService.remove(this.breadcrumb);
  }

  checkConfigurationExists(): void {
    this.componentState.setLoading();
    this.sageHrConfigurationApiService.getSageHrConfiguration().subscribe(sageHrCredentials => {
      this.sageHrConfiguration = sageHrCredentials;
      if(this.sageHrConfiguration){
        this.sortConfigurationSyncErrors();
      }
      
      this.workflowState = sageHrCredentials !== null ? WorkflowState.CONFIGURE : WorkflowState.WELCOME;
      this.componentState.setSuccess();
    });
  }

  sortConfigurationSyncErrors() {
    this.sageHrConfiguration.errors = this.sageHrConfiguration.errors.sort((a, b) =>
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }

  submitSetupForm(): void {
    this.setUpFormState.setLoading();
    this.setupFormSubmitted = true;
    if (this.setupForm.invalid) {
      this.setUpFormState.setSuccess();
      return;
    }

    const createSageHrCredentials = this.buildCreateSageHrCredentialsDto();
    this.sageHrApiService.testSageHrCredentials(createSageHrCredentials).subscribe(() => {
      this.sageHrConfigurationApiService.createSageHrConfiguration(createSageHrCredentials).subscribe(sageHrCredentials => {
        this.sageHrConfiguration = sageHrCredentials;
        this.workflowState = WorkflowState.SUCCESS;
        this.setUpFormState.setSuccess();
      }, _error => this.setUpFormState.setSuccess());
    }, _error => this.setUpFormState.setSuccess());
  }

  private buildCreateSageHrCredentialsDto = (): CreateSageHrConfiguration => ({
    sageHrCompanyId: this.sageHrCompanyId.value,
    apiKey: this.apiKey.value
  });

  beginManualSync() {
    this.syncButtonState.setLoading();
    this.sageHrApiService.startManualSync().subscribe(() => {
      this.syncButtonState.setSuccess();
      this.swalUtils.displaySuccessSwal({
        title: 'Sync Started',
        text: 'This might take a while, we\'ll send you a notification once it\'s done.'
      });
    }, error => this.syncButtonState.setSuccess());
  }

  confirmBeginManualSync() {
    this.swalUtils.displaySuccessConfirmationSwal({
      text: 'Your people\'s employee information will be retrieved from Sage HR and synchronised with Frankli.'
    }).then(response => {
      if (response.isConfirmed) {
        this.syncButtonState.setLoading();
        this.sageHrApiService.startManualSync().subscribe(() => {
          this.sageHrConfiguration.syncing = true;
          this.syncButtonState.setSuccess();
          this.swalUtils.displaySuccessSwal({
            title: 'Sync Started',
            text: 'This might take a while, feel free to leave this page and we\'ll send you a notification once the sync has completed.'
          });
        }, _error => this.syncButtonState.setSuccess());
      }
    });
  }

  confirmDisableAutomaticSync() {
    this.swalUtils.displayWarningConfirmationSwal({
      text: 'Your existing people in Frankli will not be kept up to date with any new changes in Sage HR, but you can still manually sync them from this page.'
    }).then(response => {
      if (response.isConfirmed) {
        this.toggleSyncButtonState.setLoading();
        const updateSageHrConfiguration: UpdateSageHrConfiguration = {
          automaticSyncEnabled: false
        };
        this.sageHrConfigurationApiService.updateSageHrConfiguration(updateSageHrConfiguration).subscribe(configuration => {
          this.sageHrConfiguration = configuration;
          this.toggleSyncButtonState.setSuccess();
          this.notifyUtils.notify('Automatic syncing has been successfully disabled');
        }, _error => this.toggleSyncButtonState.setSuccess());
      }
    });
  }

  confirmEnableAutomaticSync() {
    this.swalUtils.displaySuccessConfirmationSwal({
      text: 'Changes you make to your people in Sage HR will be synchronised with Frankli.'
    }).then(response => {
      if (response.isConfirmed) {
        this.toggleSyncButtonState.setLoading();
        const updateSageHrConfiguration: UpdateSageHrConfiguration = {
          automaticSyncEnabled: true
        };
        this.sageHrConfigurationApiService.updateSageHrConfiguration(updateSageHrConfiguration).subscribe(configuration => {
          this.sageHrConfiguration = configuration;
          this.toggleSyncButtonState.setSuccess();
          this.notifyUtils.notify('Automatic syncing has been successfully enabled');
        }, _error => this.toggleSyncButtonState.setSuccess());
      }
    });
  }

  confirmDeleteConfiguration() {
    this.swalUtils.displayWarningConfirmationSwal({
      text: 'You will not be able to sync your people with Sage HR until you set up the integration again. The people that you have already synced with Sage HR will remain in Frankli.'
    }).then(response => {
      if (response.isConfirmed) {
        this.componentState.setLoading();
        this.sageHrConfigurationApiService.deleteSageHrConfiguration().subscribe(() => {
          this.notifyUtils.notify('Your Sage HR configuration has been successfully deleted');
          this.checkConfigurationExists();
        }, _error => this.componentState.setSuccess());
      }
    });
  }

  getNextSyncDateTime(): Date {
    return moment(this.sageHrConfiguration.lastSyncTimestamp).add(15, 'minutes').toDate();
  }

}
